package com.havn.app.features.clients.domain

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json

private val qrPayloadJson = Json {
    encodeDefaults = false
    ignoreUnknownKeys = true
}

@Serializable
data class HavnClientQrPayload(
    @SerialName("sourceApp") val sourceApp: String = "HAVN",
    val contactId: String? = null,
    val passIdentityId: String? = null,
    val passKey: String
) {
    val clientId: String
        get() = contactId.orEmpty()

    fun encode(): String = qrPayloadJson.encodeToString(
        HavnContactQrPayloadJson(
            s = sourceApp,
            v = 2,
            c = contactId.takeIf { passIdentityId.isNullOrBlank() },
            i = passIdentityId,
            p = passKey.take(16)
        )
    )

    companion object {
        fun forClient(clientId: String, passKey: String, passIdentityId: String? = null): HavnClientQrPayload =
            HavnClientQrPayload(contactId = clientId, passIdentityId = passIdentityId, passKey = passKey)

        fun decode(raw: String): Result<HavnClientQrPayload> = runCatching {
            val payload = qrPayloadJson.decodeFromString<HavnContactQrPayloadJson>(raw.trim())
            val resolvedSource = payload.s ?: payload.sourceApp
            val resolvedContactId = payload.c ?: payload.contactId ?: payload.clientId
            val resolvedPassIdentityId = payload.i ?: payload.passIdentityId
            val resolvedPassKey = payload.p ?: payload.passKey
            require(resolvedSource == "HAVN") { "This QR code is not from HAVN." }
            require(!resolvedContactId.isNullOrBlank() || !resolvedPassIdentityId.isNullOrBlank()) {
                "This HAVN QR code is missing its contact identity."
            }
            require(!resolvedPassKey.isNullOrBlank()) { "This HAVN QR code is missing the Wallet pass key." }
            HavnClientQrPayload(
                sourceApp = resolvedSource,
                contactId = resolvedContactId,
                passIdentityId = resolvedPassIdentityId,
                passKey = resolvedPassKey
            )
        }
    }
}

@Serializable
private data class HavnContactQrPayloadJson(
    val s: String? = null,
    val v: Int? = null,
    val c: String? = null,
    val i: String? = null,
    val p: String? = null,
    @SerialName("sourceApp") val sourceApp: String? = null,
    val contactId: String? = null,
    val clientId: String? = null,
    val passIdentityId: String? = null,
    val passKey: String? = null
)
